<script setup>
// KPI compacto de Leads na Overview de Relatórios. Só aparece quando
// FEATURE_FLAGS.CRM está habilitada na conta. Consome o builder de funil
// sem pipeline_id (agregado da conta) para os últimos 30 dias.
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import FunnelReportsAPI from 'dashboard/api/funnelReports';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledOnAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const isCrmEnabled = computed(() =>
  isFeatureEnabledOnAccount.value(accountId.value, FEATURE_FLAGS.CRM)
);

const data = ref(null);
const isLoading = ref(false);

async function fetchSummary() {
  if (!isCrmEnabled.value) return;
  isLoading.value = true;
  try {
    const since = new Date();
    since.setDate(since.getDate() - 30);
    const res = await FunnelReportsAPI.show({
      since: since.toISOString(),
      until: new Date().toISOString(),
    });
    data.value = res?.data || null;
  } catch {
    data.value = null;
  } finally {
    isLoading.value = false;
  }
}

onMounted(fetchSummary);
watch(isCrmEnabled, fetchSummary);

const summary = computed(() => data.value?.summary || {});

const formatNumber = v => Number(v || 0).toLocaleString('pt-BR');
const formatMoneyCents = cents =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    (Number(cents) || 0) / 100
  );
</script>

<template>
  <section v-if="isCrmEnabled" class="flex flex-col gap-3">
    <header class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <i class="i-lucide-target size-4 text-n-brand" />
        <h2 class="text-heading-3 text-n-slate-12 mb-0">
          {{ t('OVERVIEW_REPORTS.CRM_KPI.TITLE') }}
        </h2>
        <span class="text-body-small text-n-slate-11"
          >· {{ t('OVERVIEW_REPORTS.CRM_KPI.WINDOW_30D') }}</span
        >
      </div>
      <router-link
        :to="`/app/accounts/${accountId}/reports/funnel`"
        class="text-body-small text-n-brand hover:underline inline-flex items-center gap-1"
      >
        {{ t('OVERVIEW_REPORTS.CRM_KPI.GO_TO_FUNNEL') }}
        <i class="i-lucide-arrow-right size-3" />
      </router-link>
    </header>

    <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div
        class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-4 py-3"
      >
        <p
          class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
        >
          {{ t('OVERVIEW_REPORTS.CRM_KPI.OPEN') }}
        </p>
        <p class="text-heading-3 text-n-slate-12 mb-0">
          <Spinner v-if="isLoading" size="sm" />
          <template v-else>{{ formatNumber(summary.in_pipeline) }}</template>
        </p>
        <p class="text-body-small text-n-slate-10 mb-0">
          {{ t('OVERVIEW_REPORTS.CRM_KPI.OPEN_HINT') }}
        </p>
      </div>

      <div
        class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-4 py-3"
      >
        <p
          class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
        >
          {{ t('OVERVIEW_REPORTS.CRM_KPI.WON_AMOUNT') }}
        </p>
        <p class="text-heading-3 text-n-grass-11 mb-0">
          <Spinner v-if="isLoading" size="sm" />
          <template v-else>{{
            formatMoneyCents(summary.won_amount_cents)
          }}</template>
        </p>
        <p class="text-body-small text-n-slate-10 mb-0">
          {{
            t('OVERVIEW_REPORTS.CRM_KPI.WON_COUNT', {
              count: formatNumber(summary.won),
            })
          }}
        </p>
      </div>

      <div
        class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-4 py-3"
      >
        <p
          class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
        >
          {{ t('OVERVIEW_REPORTS.CRM_KPI.AVG_TICKET') }}
        </p>
        <p class="text-heading-3 text-n-slate-12 mb-0">
          <Spinner v-if="isLoading" size="sm" />
          <template v-else>{{
            formatMoneyCents(summary.avg_ticket_cents)
          }}</template>
        </p>
        <p class="text-body-small text-n-slate-10 mb-0">
          {{ t('OVERVIEW_REPORTS.CRM_KPI.AVG_TICKET_HINT') }}
        </p>
      </div>

      <div
        class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-4 py-3"
      >
        <p
          class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1"
        >
          {{ t('OVERVIEW_REPORTS.CRM_KPI.CONVERSION') }}
        </p>
        <p class="text-heading-3 text-n-slate-12 mb-0">
          <Spinner v-if="isLoading" size="sm" />
          <template v-else
            >{{ (summary.conversion_rate || 0).toFixed(1) }}%</template
          >
        </p>
        <p class="text-body-small text-n-slate-10 mb-0">
          {{ t('OVERVIEW_REPORTS.CRM_KPI.CONVERSION_HINT') }}
        </p>
      </div>
    </div>
  </section>
</template>
